
import React, { useEffect, useState, useCallback } from "react";
import { SubscriptionDetails } from "../components/SubscriptionDetails";
import { SubscriptionPlans } from "../components/SubscriptionPlans";
import { ProviderSubscriptionCard } from "../components/ProviderSubscriptionCard";
import { Separator } from "../components/ui/separator";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Info, CheckCircle, XCircle } from "lucide-react";
import { useSubscription } from "../hooks/useSubscription";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from 'react-i18next';


const Subscription = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const [showPaymentResult, setShowPaymentResult] = useState(false);
  const [paymentResult, setPaymentResult] = useState<{
    status: 'success' | 'error' | 'cancelled';
    title: string;
    message: string;
    Icon: React.ElementType;
  } | null>(null);
  const { t } = useTranslation();

  const handlePayPalResponse = useCallback((success: string | null) => {
    if (success === null) return;

    setShowPaymentResult(true);

    if (success === "true") {
      const queryParams = new URLSearchParams(location.search);
      const tier = queryParams.get("tier") || "";
      const ref = queryParams.get("reference") || "";
      navigate(`/subscription-success?plan=${tier}&reference=${ref}`, { replace: true });
      return;
    } else if (success === "false") {
      setPaymentResult({
        status: 'cancelled',
        title: t('subscription.paymentCancelled'),
        message: t('subscription.paymentCancelledDescription'),
        Icon: XCircle
      });

      toast.error(t('subscription.paymentCancelled'), {
        description: t('subscription.paymentCancelledDescription'),
        duration: 6000
      });

      setTimeout(() => {
        setShowPaymentResult(false);
        // Clean up URL without hard refresh for cancelled payments
        window.history.replaceState({}, document.title, "/subscription");
      }, 3000);
    }
  }, [t]);

  useEffect(() => {
    if (!user) {
      console.log("No user found, redirecting to auth");
      navigate('/auth');
      return;
    }

    const queryParams = new URLSearchParams(location.search);

    // Only handle PayPal responses now
    const success = queryParams.get("success");
    if (success !== null) {
      handlePayPalResponse(success);
    }
  }, [location.search, user, navigate, handlePayPalResponse]);

  if (showPaymentResult && paymentResult) {
    const colorClasses = {
      success: 'text-green-500',
      error: 'text-red-500',
      cancelled: 'text-gray-500'
    };
    const titleColorClasses = {
      success: 'text-green-700',
      error: 'text-red-700',
      cancelled: 'text-gray-700'
    };

    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <Card>
            <CardContent className="p-8 text-center">
              <paymentResult.Icon className={`h-16 w-16 ${colorClasses[paymentResult.status]} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${titleColorClasses[paymentResult.status]} mb-2`}>{paymentResult.title}</h2>
              <p className="text-gray-600 mb-4">
                {paymentResult.message}
              </p>
              <Button onClick={() => {
                setShowPaymentResult(false);
                if (paymentResult.status === 'success') {
                  window.location.reload();
                }
              }}>
                {t('subscription.close')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium mb-1">{t('subscription.newBillingOptions')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('subscription.newBillingDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <ProviderSubscriptionCard />

        <SubscriptionDetails />

        <Separator />


        <SubscriptionPlans />
      </div>
    </div>
  );
};

export default Subscription;
